import * as React from 'react'
import {ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {Nullable} from '../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {
    GuestApplyButton,
    GuestBackButton,
    GuestPlainLinkStyledBackButton,
    PageTitle,
} from '../common/NatOwnershipTransfersCommon'
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatOwnershipTransferOldMemberDetail} from '../repository/NatGetOwnershipTransferOldMember'
import {NatPostOldMemberResponse} from '../repository/NatPostOldMemberRequest'
import {
    NatOwnershipTransferOldMemberInfo,
    NatOwnershipTransferOldMemberLocationState,
} from './oldMember/NatGuestOldMemberInterface'
import NatGuestOldMemberConfirm from './oldMember/NatGuestOldMemberConfirm'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    applyId?: string
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<NatOwnershipTransferOldMemberLocationState>
}

interface States {
    oldMemberDetail: NatOwnershipTransferOldMemberInfo
    isLoadCompleted: boolean
}

class NatGuestOldMemberConfirmPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            oldMemberDetail: {
                ownershipTransferDate: '',
                accessExpirationDate: '',
                oldMemberFamilyName: '',
                oldMemberFirstName: '',
                moveOutDate: '',
                phoneNumberAfterMoveOut: '',
                zipCodeAfterMoveOut: '',
                prefectureAfterMoveOut: '',
                address2AfterMoveOut: '',
                address3AfterMoveOut: '',
                buildingRoomNumberAfterMoveOut: '',
                oldMemberMailAddress: '',
                oldMemberMailAddressConfirm: '',
                version: 0,
            },
            isLoadCompleted: false,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleEditButtonOnClick = this.handleEditButtonOnClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        if (!this.props.applyId && !this.props.location.state?.applyInfo) {
            return
        }

        let oldMemberInfo = null
        if (this.props.location.state?.applyInfo) {
            // 引継情報を使用
            oldMemberInfo = {
                ownershipTransferDate: this.props.location.state.ownershipTransferDate,
                accessExpirationDate: '',
                oldMemberFamilyName: this.props.location.state.applyInfo.oldMemberFamilyName,
                oldMemberFirstName: this.props.location.state.applyInfo.oldMemberFirstName,
                moveOutDate: this.props.location.state.applyInfo.moveOutDate,
                phoneNumberAfterMoveOut: this.props.location.state.applyInfo.phoneNumberAfterMoveOut,
                zipCodeAfterMoveOut: this.props.location.state.applyInfo.zipCodeAfterMoveOut,
                prefectureAfterMoveOut: this.props.location.state.applyInfo.prefectureAfterMoveOut,
                address2AfterMoveOut: this.props.location.state.applyInfo.address2AfterMoveOut,
                address3AfterMoveOut: this.props.location.state.applyInfo.address3AfterMoveOut,
                buildingRoomNumberAfterMoveOut: this.props.location.state.applyInfo.buildingRoomNumberAfterMoveOut,
                oldMemberMailAddress: this.props.location.state.applyInfo.oldMemberMailAddress,
                oldMemberMailAddressConfirm: this.props.location.state.applyInfo.oldMemberMailAddressConfirm,
                version: this.props.location.state.applyInfo.version,
            }
        } else if (this.props.applyId) {
            // 取得APIを呼んでServerからの取得情報を使用
            const apiResponse = await this.getOwnershipTransferOldMember(this.props.applyId)
            if (apiResponse) {
                oldMemberInfo = {
                    ownershipTransferDate: apiResponse.applyInfo.ownershipTransferDate,
                    accessExpirationDate: apiResponse.oldMemberInfo.accessExpirationDate,
                    oldMemberFamilyName: apiResponse.oldMemberInfo.oldMemberFamilyName,
                    oldMemberFirstName: apiResponse.oldMemberInfo.oldMemberFirstName,
                    moveOutDate: apiResponse.oldMemberInfo.moveOutDate,
                    phoneNumberAfterMoveOut: apiResponse.oldMemberInfo.phoneNumberAfterMoveOut,
                    zipCodeAfterMoveOut: apiResponse.oldMemberInfo.zipCodeAfterMoveOut ?? '',
                    prefectureAfterMoveOut: apiResponse.oldMemberInfo.prefectureAfterMoveOut,
                    address2AfterMoveOut: apiResponse.oldMemberInfo.address2AfterMoveOut,
                    address3AfterMoveOut: apiResponse.oldMemberInfo.address3AfterMoveOut,
                    buildingRoomNumberAfterMoveOut: apiResponse.oldMemberInfo.buildingRoomNumberAfterMoveOut ?? '',
                    oldMemberMailAddress: apiResponse.oldMemberInfo.oldMemberMailAddress ?? '',
                    oldMemberMailAddressConfirm: apiResponse.oldMemberInfo.oldMemberMailAddress ?? '',
                    version: apiResponse.oldMemberInfo.version,
                }
            }
        }
        if (oldMemberInfo) {
            this.setState({
                oldMemberDetail: oldMemberInfo,
            })
        }
    }

    async getOwnershipTransferOldMember(applyId: string): Promise<Nullable<NatOwnershipTransferOldMemberDetail>> {
        const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransferOldMember
                .bind(ownershipTransfersRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    // 修正するボタンを押下
    handleEditButtonOnClick(): void {
        this.props.navigate(
            `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/input/${this.props.applyId ?? ''}`,
            {
                replace: true,
                state: {
                    applyInfo: this.state.oldMemberDetail,
                    ownershipTransferDate: this.props.location.state.ownershipTransferDate,
                },
            },
        )
    }

    async handleRegisterButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        const {ownershipTransfersRepo, condominiumCd, applyId} = this.props
        const {
            oldMemberFamilyName, oldMemberFirstName, moveOutDate,
            phoneNumberAfterMoveOut, zipCodeAfterMoveOut, prefectureAfterMoveOut,
            address2AfterMoveOut, address3AfterMoveOut, buildingRoomNumberAfterMoveOut,
            oldMemberMailAddress, oldMemberMailAddressConfirm, version,
        } = this.state.oldMemberDetail

        const request = {
            applyId: applyId ?? null,
            familyName: oldMemberFamilyName,
            firstName: oldMemberFirstName,
            moveOutDate: moveOutDate,
            phoneNumberAfterMoveOut: phoneNumberAfterMoveOut,
            zipCodeAfterMoveOut: zipCodeAfterMoveOut,
            prefectureAfterMoveOut: prefectureAfterMoveOut,
            address2AfterMoveOut: address2AfterMoveOut,
            address3AfterMoveOut: address3AfterMoveOut,
            buildingRoomNumberAfterMoveOut: buildingRoomNumberAfterMoveOut,
            mailAddress: oldMemberMailAddress,
            mailAddressConfirm: oldMemberMailAddressConfirm,
            version: version,
            confirmed: true,
        }

        try {
            const response: Nullable<NatPostOldMemberResponse> =
                await this.props.asyncCallFunction(
                    ownershipTransfersRepo.applyOwnershipTransferOldMember.bind(
                        ownershipTransfersRepo, condominiumCd, request),
                    () => {
                        this.props.navigate(`/guest/error`, {replace: true})
                    },
                )
            if (response && response.requestError === null) {
                // 提出完了画面
                this.props.navigate(
                    `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/complete`,
                    {
                        replace: true,
                        state: {
                            applyId: response.details.applyId,
                            accessExpirationDate: response.details.accessExpirationDate,
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleBackLinkOnClick(): Promise<void> {
        const locationState: NatOwnershipTransferOldMemberLocationState = this.props.location.state
        if (this.props.location.state?.isApply) {
            this.props.navigate(
                `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/input/${this.props.applyId ?? ''}`,
                {
                    replace: true,
                    state: JSON.parse(JSON.stringify(locationState)),
                },
            )
        } else {
            const {condominiumCd, ownershipTransfersRepo, eventEmitter} = this.props
            await eventEmitter.showLoading()
            try {
                const accessKeyByLoginUser = await this.props.asyncCallFunction(
                    ownershipTransfersRepo.getAccessKeyByLoginUser.bind(ownershipTransfersRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                const accessKey = accessKeyByLoginUser?.details?.accessKey ?? ''
                this.props.navigate(`/guest/mypage?accessKey=${accessKey}`, {replace: true})
            } finally {
                eventEmitter.closeLoading()
            }
        }
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                {(this.props.location.state?.isApply ||
                        !this.props.location.state?.isCompleteFlow) &&
                    <GuestPlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>
                        戻る
                    </GuestPlainLinkStyledBackButton>
                }
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容の確認
                </PageTitle>
                <Box height={'20px'}/>
                <NatGuestOldMemberConfirm
                    {...this.state.oldMemberDetail}
                    applyId={this.props.applyId}
                    isApply={this.props.location.state?.isApply}
                />
                {this.props.location.state?.isApply &&
                    <>
                        <BlockSpacer height={'20px'}/>
                        <GuestApplyButton onClick={this.handleRegisterButtonClick}>
                            申請を提出する
                        </GuestApplyButton>
                        <Box height={'30px'}/>
                        <GuestBackButton
                            onClick={this.handleEditButtonOnClick}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    修正する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </GuestBackButton>
                    </>
                }
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestOldMemberConfirmPage)
